import { useBreadcrumbStore } from "@/store/useBreadcrumbStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { useLogout } from "@/utils/authHelpers";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Avatar } from "@onengine/component-library";
import { MOBILE_NAVBAR_BREAKPOINT, useIsMobileNavbar } from "@/hooks/useIsMobileNavbar";
import {
  ActionIcon,
  AppShell,
  Box,
  Group,
  LoadingOverlay,
  Popover,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure, useHotkeys } from "@mantine/hooks";
import { IconMenu2 } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumbs } from "./Breadcrumbs/Breadcrumbs";
import { HeaderChatButton } from "./HeaderActions/HeaderChatButton";
import { NotificationBell } from "./HeaderActions/NotificationBell";
import Sidebar from "./Sidebar/Sidebar";
import { showErrorAlert } from "@/utils/responseHandler";

type BreadcrumbItem = {
  title: string;
  realHref: string;
  maskedHref: string;
  icon?: React.ReactNode;
};

export default function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(true);
  const routerState = useRouterState();
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  const currentMaskedPath =
    routerState.location.maskedLocation?.pathname ?? routerState.location.pathname;
  const currentPath = routerState.location.pathname;

  const sidebarWidthOpened = 240;
  const sidebarWidthClosed = 72;
  const headerHeight = 56;
  const headerAndMainPadding = "md";
  const mobileHeaderPadding = 4;
  const mobileMainPadding = 8;
  const isMobile = useIsMobileNavbar();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false);
  const drawerOpened = isMobile && mobileOpened;

  useEffect(() => {
    if (!isMobile) closeMobile();
  }, [isMobile, closeMobile]);

  useHotkeys([
    [
      "Escape",
      (event) => {
        if (drawerOpened && !event.defaultPrevented) closeMobile();
      },
      { preventDefault: false },
    ],
  ]);

  // Get custom breadcrumbs from store
  const customBreadcrumbs = useBreadcrumbStore((state) => state.items);
  const isCustomBreadcrumb = useBreadcrumbStore((state) => state.isCustomBreadcrumb);
  const setCustomBreadcrumb = useBreadcrumbStore((state) => state.setCustomBreadcrumb);

  // Track previous path to detect route changes
  const prevPathRef = useRef(currentPath);

  // Clear custom breadcrumb flag on route change to allow auto-generation
  // The new page's useBreadcrumb will set it back if needed
  useEffect(() => {
    if (prevPathRef.current !== currentPath) {
      setCustomBreadcrumb(false);
      closeMobile();
      prevPathRef.current = currentPath;
    }
  }, [currentPath, setCustomBreadcrumb, closeMobile]);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    // If custom breadcrumbs are set AND flag is true, use them
    if (customBreadcrumbs.length > 0 && isCustomBreadcrumb) {
      return customBreadcrumbs.map((item) => ({
        title: item.title,
        realHref: item.href || "#",
        maskedHref: item.href || "#",
        icon: item.icon,
      }));
    }
    const realSegments = currentPath.split("/").filter(Boolean);
    const maskedSegments = currentMaskedPath.split("/").filter(Boolean);
    if (realSegments.length === 0) {
      return [
        {
          title: "Home",
          realHref: "/",
          maskedHref: "/",
          icon: getRouteIcon("", 16),
        },
      ];
    }
    let accRealPath = "";
    let accMaskedPath = "";

    return realSegments.map((realSeg, idx) => {
      const maskedSeg = maskedSegments[idx] ?? realSeg;

      accRealPath += `/${realSeg}`;
      accMaskedPath += `/${maskedSeg}`;

      return {
        title: decodeURIComponent(maskedSeg).replace(/-/g, " "),
        realHref: accRealPath,
        maskedHref: accMaskedPath,
        icon: idx === 0 ? getRouteIcon(realSeg as any, 16) : undefined,
      };
    });
  }, [currentPath, currentMaskedPath, customBreadcrumbs, isCustomBreadcrumb]);

  // Track if the user is navigating away from the page - used to unmount components which are subscribed to permissions api
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const queryClient = useQueryClient();
  const { mutateAsync: signOut, isPending: isLoggingOut } = useLogout();
  const handleSignOut = async () => {
    if (isImpersonating) {
      showErrorAlert(
        "Please end impersonation before logging out",
        "Cannot logout while impersonating",
      );
      return;
    }
    setIsNavigatingAway(true); // used only for unmounting component
    await signOut();
    queryClient.clear();
    await navigate({ to: "/login" });
  };

  const mainPadding = isMobile ? mobileMainPadding : headerAndMainPadding;
  const headerBreadcrumbItems = isMobile
    ? [{ ...breadcrumbItems[breadcrumbItems.length - 1], icon: breadcrumbItems[0]?.icon }]
    : breadcrumbItems;

  if (isLoggingOut || isNavigatingAway) {
    return <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />;
  }

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <AppShell
        navbar={{
          width: opened ? sidebarWidthOpened : sidebarWidthClosed,
          breakpoint: MOBILE_NAVBAR_BREAKPOINT,
          collapsed: { mobile: !drawerOpened },
        }}
        layout="alt"
        padding={0}
        style={{ flex: 1, minHeight: 0 }}
      >
        <AppShell.Header
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-3)",
            top: "initial",
          }}
          px={isMobile ? mobileHeaderPadding : headerAndMainPadding}
        >
          <Group h={headerHeight} justify="space-between" wrap="nowrap">
            <Group gap={4} wrap="nowrap" style={{ minWidth: 0, overflow: "hidden" }}>
              {isMobile && (
                <ActionIcon
                  variant="subtle"
                  size={44}
                  c="var(--icon-default)"
                  onClick={toggleMobile}
                  aria-label="Open navigation"
                  aria-expanded={drawerOpened}
                  aria-controls="app-navbar"
                >
                  <IconMenu2 size={20} />
                </ActionIcon>
              )}
              <Breadcrumbs items={headerBreadcrumbItems} showLinkButton={!isMobile} />
            </Group>
            <Group gap="xs" wrap="nowrap">
              <HeaderChatButton />
              <NotificationBell />
              <Popover withArrow shadow="md">
                <Popover.Target>
                  <Avatar />
                </Popover.Target>
                <Popover.Dropdown>
                  <UnstyledButton onClick={handleSignOut}>Logout</UnstyledButton>
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Group>
        </AppShell.Header>
        <AppShell.Navbar
          id="app-navbar"
          inert={isMobile && !drawerOpened}
          style={{
            position: "fixed",

            top: "initial",
          }}
        >
          <Sidebar
            opened={opened}
            onToggle={() => setOpened((o) => !o)}
            isMobile={isMobile}
            onClose={closeMobile}
          />
        </AppShell.Navbar>
        <AppShell.Main
          style={{
            paddingTop: headerHeight,
            overflow: "auto",
            flex: 1,
            minHeight: "initial",
          }}
          data-slot="app-shell-main"
        >
          <Box p={mainPadding} style={{ height: "100%" }} data-slot="app-shell-main-content">
            {children}
          </Box>
        </AppShell.Main>
      </AppShell>
    </Box>
  );
}
